import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  type DriverGroupChangeView,
  type DriverGroupView,
  type WalletAdjustmentReasonCode,
  type WalletAdjustmentView,
  type WalletAdminDetail,
  type WalletCeilingChangeView,
  type WalletChangeStatus,
  type WalletCreditPackageView,
  type WalletMarketRow,
  type WalletMoneyWire,
  type SettlementModeCode,
  SETTLEMENT_MODES,
  WALLET_ADJUSTMENT_REASON_CODES,
} from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  adjustmentDraftIssue,
  formatMarketInstant,
  isDriverId,
  marketTimeZone,
  packageDiscountPercent,
  packageDraftIssue,
  sortGroups,
  summarizeWallets,
  walletApproveBlock,
  walletAuthority,
  walletChangeTone,
  walletStatusTone,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { LoadingBlock } from '../components/loading-block';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, MoneyInput, Notice, PageHeader, Panel, SegmentedControl, Select, StatCard, Table, TextInput, FormRow, EmptyState, Stack, Row, Text, Grid, InlineRow, PanelBody, MonoInput } from '@vello/web-theme';

/**
 * Finance › Carteiras pré-pagas and Motoristas › Grupos (W8-DRIVER-PREPAID-WALLET, D459, ADR-0074; finding C-05; owner
 * answer D435 §5.0 item 4), over `WalletAdminApi` (`/v1/admin/wallet/*`, `/v1/admin/driver-groups*`).
 *
 * Carteiras (per market, chosen from the operator's scope — never typed):
 * - **Carteiras**: every driver's standing against the debt ceiling that governs him (driver → group → market → country,
 *   the smallest binds), with exact per-currency debt totals and how many are warned / have offers paused; opening one
 *   shows his statement, top-ups and changes, and lets the maker PROPOSE a credit/debit adjustment (a stable reason and a
 *   note) or a driver ceiling override (or clear it).
 * - **Aprovações**: the four-eyes inbox of adjustments and ceiling overrides. Approve is hidden from the operator who
 *   proposed it (the server refuses `SELF_APPROVAL` anyway) and from one without `wallet:approve`, with the reason shown;
 *   a rejection needs a reason. Nothing moves until a second operator approves.
 * - **Pacotes**: the credit packages of the market and of its country (credit, price, the discount between them), a new
 *   one or a revision (a new version), and taking one off sale.
 * Grupos: the market's driver groups — create, rename, archive/restore, members in and out, and a group ceiling override
 * proposal (four eyes, like the driver's).
 * Money is typed through the design-system `MoneyInput` in the market's currency (never minor units, never a typed
 * currency). A refused or failed read is an ERROR — never an empty list or a zero. No native dialog is ever opened.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;
type Tab = 'wallets' | 'changes' | 'packages';

const note = (text: string): ReactElement => <EmptyState title={text} />;

/** The header scope's market (owner 2026-10-12: no second picker) + the market's currency formatter + the refusal copy, shared by both screens. */
function useWalletMarket(): {
  /** The scoped market, or `''` while the scope names none (the screens then show the scope notice). */
  market: string;
  scopeMarket: string | undefined;
  choices: readonly { id: string; name: string }[];
  money: (m: WalletMoneyWire | null | undefined) => string;
  refusal: (e: { code: string; reasonCode?: string; detail?: string }, fallback: string) => string;
} {
  const { t, lang } = useT();
  const { fail, refusal: refusalOf } = useVocab();
  const { market: scopeMarket, choices } = useScopeMarket();
  const money = useCallback(
    (m: WalletMoneyWire | null | undefined): string =>
      m === null || m === undefined ? '—' : /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${m.amountMinor} ${m.currency}`,
    [lang],
  );
  const refusal = useCallback(
    (e: { code: string; reasonCode?: string; detail?: string }, fallback: string): string => {
      return refusalOf('refusal.admin.driver_wallet', e) ?? t(fallback, { code: fail(e) });
    },
    [t, refusalOf],
  );
  return { market: scopeMarket ?? '', scopeMarket, choices, money, refusal };
}

/** A ceiling override proposal (driver or group): an amount in the market's currency, or "clear it", with a note. */
function CeilingProposal({ idPrefix, currency, onPropose, busy }: { idPrefix: string; currency: string; onPropose: (ceilingMinor: string | null, note: string) => void; busy: boolean }): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [clear, setClear] = useState(false);
  const [text, setText] = useState('');
  const ready = (clear || amount !== undefined) && text.trim().length >= 3;
  return (
    <FormRow
      action={
        <Button type="button" disabled={!ready || busy} onClick={() => onPropose(clear ? null : (amount ?? 0n).toString(), text.trim())}>{t('wallet.ceiling.propose')}</Button>
      }
    >
      {!clear && isIso4217Currency(currency) && (
        <MoneyInput id={`${idPrefix}-ceiling`} label={t('wallet.ceiling.amount')} currency={currency} locale={lang} valueMinor={amount} onChangeMinor={setAmount} issueText={(i) => v('format.money_input_issue', i)} />
      )}
      <Field label={t('wallet.ceiling.mode')} htmlFor={`${idPrefix}-ceiling-mode`}>
        <Select id={`${idPrefix}-ceiling-mode`} value={clear ? 'clear' : 'set'} onChange={(e) => setClear(e.target.value === 'clear')}>
          <option value="set">{t('wallet.ceiling.set')}</option>
          <option value="clear">{t('wallet.ceiling.clear')}</option>
        </Select>
      </Field>
      <Field label={t('wallet.note')} htmlFor={`${idPrefix}-ceiling-note`} hint={t('wallet.note.hint')}>
        <TextInput id={`${idPrefix}-ceiling-note`} value={text} maxLength={500} onChange={(e) => setText(e.target.value)} />
      </Field>
    </FormRow>
  );
}

export function DriverWalletsScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v } = useVocab();
  const { mayView, mayManage, mayApprove } = walletAuthority(grants);
  const { market, scopeMarket, choices, money, refusal } = useWalletMarket();
  const [tab, setTab] = useState<Tab>('wallets');
  const [loaded, setLoaded] = useState('');
  const [currency, setCurrency] = useState('');
  const [wallets, setWallets] = useState<readonly WalletMarketRow[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [detail, setDetail] = useState<WalletAdminDetail | undefined>(undefined);
  /** The driver whose wallet the drawer shows (set at the click, so the drawer opens while the statement is read). */
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [changeStatus, setChangeStatus] = useState<WalletChangeStatus>('PENDING');
  const [changes, setChanges] = useState<{ adjustments: readonly WalletAdjustmentView[]; ceilingChanges: readonly WalletCeilingChangeView[] } | undefined>(undefined);
  const [packages, setPackages] = useState<readonly WalletCreditPackageView[] | undefined>(undefined);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  // adjustment draft
  const [adjDirection, setAdjDirection] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [adjAmount, setAdjAmount] = useState<bigint | undefined>(undefined);
  const [adjReason, setAdjReason] = useState<WalletAdjustmentReasonCode | ''>('');
  const [adjNote, setAdjNote] = useState('');
  // package draft
  const [pkgScope, setPkgScope] = useState<'market' | 'country'>('market');
  const [pkgCredit, setPkgCredit] = useState<bigint | undefined>(undefined);
  const [pkgPrice, setPkgPrice] = useState<bigint | undefined>(undefined);
  const [pkgSort, setPkgSort] = useState('0');
  const seq = useRef(0);

  const load = useCallback(
    async (m: string, background = false): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      // A background tick keeps the rows (and the opened wallet) on screen; a new market starts clean.
      if (!background) {
        setLoaded(target);
        setWallets(undefined);
        setChanges(undefined);
        setPackages(undefined);
        setDetail(undefined);
        setOpenId(undefined);
        setError(undefined);
      }
      if (target === '' || !mayView) return;
      const [w, c, p] = await Promise.all([runtime.wallet.wallets(target), runtime.wallet.changes(target, changeStatus), runtime.wallet.packages(target)]);
      if (mine !== seq.current) return;
      if (w.ok) {
        setError(undefined);
        setWallets(w.value.wallets);
        setCurrency(w.value.currency ?? '');
      } else setError(refusal(w.error, 'wallet.error'));
      if (c.ok) setChanges({ adjustments: c.value.adjustments, ceilingChanges: c.value.ceilingChanges });
      if (p.ok) setPackages(p.value.packages);
    },
    [runtime, mayView, changeStatus, refusal],
  );

  // The wallets follow the header scope's market and stay current in the background (no "load" button).
  useEffect(() => {
    void load(market);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' && mayView });

  const reloadChanges = async (status: WalletChangeStatus): Promise<void> => {
    setChangeStatus(status);
    if (loaded === '') return;
    setChanges(undefined);
    const c = await runtime.wallet.changes(loaded, status);
    if (c.ok) setChanges({ adjustments: c.value.adjustments, ceilingChanges: c.value.ceilingChanges });
    else setMsg({ tone: 'danger', text: refusal(c.error, 'wallet.error') });
  };

  const open = async (driverId: string): Promise<void> => {
    setOpenId(driverId);
    setDetail(undefined);
    const res = await runtime.wallet.wallet(loaded, driverId);
    if (res.ok) setDetail(res.value);
    else setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
  };

  const after = async (text: string): Promise<void> => {
    setMsg({ tone: 'success', text });
    const driverId = detail?.driverId;
    await load(loaded);
    if (driverId !== undefined) await open(driverId);
  };

  const proposeAdjustment = async (): Promise<void> => {
    if (detail === undefined || adjReason === '') return;
    setBusy(true);
    const res = await runtime.wallet.proposeAdjustment({ marketId: loaded, driverId: detail.driverId, direction: adjDirection, amountMinor: (adjAmount ?? 0n).toString(), reasonCode: adjReason, note: adjNote.trim() });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
    setAdjAmount(undefined);
    setAdjNote('');
    await after(t('wallet.adjustment.proposed'));
  };

  const proposeDriverCeiling = async (ceilingMinor: string | null, text: string): Promise<void> => {
    if (detail === undefined) return;
    setBusy(true);
    const res = await runtime.wallet.proposeCeiling({ marketId: loaded, level: 'DRIVER', driverId: detail.driverId, ceilingMinor, note: text });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
    await after(t('wallet.ceiling.proposed'));
  };

  const decide = async (kind: 'adjustment' | 'ceiling', id: string, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    const reason = (reasons[id] ?? '').trim();
    if (decision === 'REJECT' && reason.length < 3) return setMsg({ tone: 'warning', text: t('wallet.refusal.reasonRequired') });
    setBusy(true);
    const input = { marketId: loaded, decision, ...(reason !== '' ? { reason } : {}) } as const;
    const res = kind === 'adjustment' ? await runtime.wallet.decideAdjustment(id, input) : await runtime.wallet.decideCeiling(id, input);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
      await reloadChanges(changeStatus);
      return;
    }
    await after(t(decision === 'APPROVE' ? 'wallet.decision.approved' : 'wallet.decision.rejected'));
  };

  const savePackage = async (existing?: WalletCreditPackageView, active?: boolean): Promise<void> => {
    setBusy(true);
    const res = await runtime.wallet.savePackage(
      existing !== undefined
        ? { marketId: loaded, scope: existing.scope, creditMinor: existing.credit.amountMinor, priceMinor: existing.price.amountMinor, sortOrder: existing.sortOrder, active: active ?? existing.active, packageId: existing.packageId, expectedVersion: existing.version }
        : { marketId: loaded, scope: pkgScope, creditMinor: (pkgCredit ?? 0n).toString(), priceMinor: (pkgPrice ?? 0n).toString(), sortOrder: Number.parseInt(pkgSort, 10) || 0, active: true },
    );
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
    if (existing === undefined) {
      setPkgCredit(undefined);
      setPkgPrice(undefined);
    }
    await after(t('wallet.package.saved'));
  };

  const tz = marketTimeZone(loaded, markets);
  const when = (iso: string | null | undefined): string => (iso === null || iso === undefined ? '—' : formatMarketInstant(iso, tz, lang));
  const summary = wallets !== undefined ? summarizeWallets(wallets) : undefined;
  const statusBadge = (s: string): ReactElement => <Badge tone={walletStatusTone(s)}>{v('driver.wallet_standing', s)}</Badge>;
  const blockText = (b: ReturnType<typeof walletApproveBlock>): string | undefined => (b === 'OWN_PROPOSAL' ? t('wallet.approve.own') : b === 'NO_PERMISSION' ? t('wallet.approve.noPermission') : undefined);
  const adjIssue = detail !== undefined ? adjustmentDraftIssue({ driverId: detail.driverId, amountMinor: adjAmount, reasonCode: adjReason, note: adjNote }) : 'DRIVER_REQUIRED';
  const pkgIssue = packageDraftIssue(pkgCredit, pkgPrice);

  const decisionCell = (kind: 'adjustment' | 'ceiling', id: string, change: { proposedBy: string; status: string }): ReactElement => {
    const block = walletApproveBlock(change, me?.id, mayApprove, selfApproval);
    if (block === 'NOT_PENDING') return <span>—</span>;
    return (
      <Stack gap={2}>
        <TextInput aria-label={t('wallet.decision.reason')} placeholder={t('wallet.decision.reason')} value={reasons[id] ?? ''} maxLength={500} onChange={(e) => setReasons((r) => ({ ...r, [id]: e.target.value }))} />
        <Row gap={2} align="center" wrap>
          {block === undefined && <Button type="button" variant="success" disabled={busy} onClick={() => void decide(kind, id, 'APPROVE')}>{t('wallet.decision.approve')}</Button>}
          {mayApprove || change.proposedBy === me?.id ? <Button type="button" variant="danger" disabled={busy} onClick={() => void decide(kind, id, 'REJECT')}>{t('wallet.decision.reject')}</Button> : null}
        </Row>
        {blockText(block) !== undefined && <Text role="caption" tone="tertiary">{blockText(block)}</Text>}
      </Stack>
    );
  };

  return (
    <>
      <PageHeader title={t('wallet.title')} subtitle={t('wallet.subtitle')} />
      {!mayView ? (
        <Panel>{note(t('wallet.noPermission'))}</Panel>
      ) : scopeMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
        <>
          {(error !== undefined || (msg !== undefined && openId === undefined)) && (
            <Panel>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && openId === undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            </Panel>
          )}

          {loaded !== '' && error === undefined && (
            <div>
              <SegmentedControl<Tab>
                ariaLabel={t('wallet.title')}
                value={tab}
                onChange={setTab}
                options={[
                  { id: 'wallets', label: t('wallet.tab.wallets') },
                  { id: 'changes', label: t('wallet.tab.changes') },
                  { id: 'packages', label: t('wallet.tab.packages') },
                ]}
              />
            </div>
          )}

          {tab === 'wallets' && (
            <>
              {summary !== undefined && (
                <Grid min="sm" gap={4}>
                  <StatCard label={t('wallet.summary.debt')} value={summary.debtByCurrency.length === 0 ? money({ amountMinor: '0', currency }) : summary.debtByCurrency.map((d) => money({ amountMinor: d.amountMinor.toString(), currency: d.currency })).join(' · ')} hint={t('wallet.summary.inDebt', { count: String(summary.inDebt) })} />
                  <StatCard label={t('wallet.summary.warned')} value={String(summary.warned)} tone={summary.warned > 0 ? 'warning' : undefined} />
                  <StatCard label={t('wallet.summary.blocked')} value={String(summary.blocked)} tone={summary.blocked > 0 ? 'danger' : undefined} />
                </Grid>
              )}
              <Panel padded={false}>
                <Table
                  columns={[
                    { key: 'driver', header: t('wallet.col.driver'), render: (w: WalletMarketRow) => <PersonName id={w.driverId} /> },
                    { key: 'status', header: t('wallet.col.status'), render: (w: WalletMarketRow) => statusBadge(w.standing.status) },
                    { key: 'balance', header: t('wallet.col.balance'), numeric: true, render: (w: WalletMarketRow) => <span className="tnum">{money(w.standing.balance)}</span> },
                    { key: 'ceiling', header: t('wallet.col.ceiling'), numeric: true, render: (w: WalletMarketRow) => <span className="tnum">{money(w.standing.ceiling)}{w.standing.source !== undefined ? ` · ${v('driver.wallet_ceiling_source', w.standing.source)}` : ''}</span> },
                  ]}
                  rows={wallets ?? []}
                  rowKey={(w) => w.driverId}
                  onRowClick={(w) => void open(w.driverId)}
                  {...(openId !== undefined ? { selectedKey: openId } : {})}
                  loading={loaded !== '' && wallets === undefined && error === undefined}
                  loadingLabel={t('ui.loading')}
                  error={error !== undefined ? t('ui.loadFailed') : undefined}
                  onRetry={() => void load(market)}
                  retryLabel={t('ui.retry')}
                  empty={note(loaded === '' ? t('wallet.needsMarket') : t('wallet.empty'))}
                />
              </Panel>

              <Drawer open={openId !== undefined} onClose={() => setOpenId(undefined)} labelledBy="wallet-detail-title">
                {openId !== undefined && (
                  <>
                    <DrawerHeader titleId="wallet-detail-title" title={<>{t('wallet.detail.title')} <PersonName id={openId} /></>} subtitle={<IdRef value={openId} copy />} onClose={() => setOpenId(undefined)} closeLabel={t('ui.close')} />
                    <DrawerBody>
                      <DrawerStack>
                        {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
                        {detail === undefined ? (
                          <LoadingBlock lines={3} />
                        ) : (
                          <>
                    <Row gap={2} align="center" wrap>
                      {statusBadge(detail.standing.status)}
                      <span>{t('wallet.detail.balance', { balance: money(detail.standing.balance), ceiling: money(detail.standing.ceiling), headroom: money(detail.standing.headroom) })}</span>
                    </Row>
                    <Table
                      columns={[
                        { key: 'when', header: t('wallet.col.when'), render: (l: WalletAdminDetail['statement'][number]) => <span className="tnum">{when(l.postedAt)}</span> },
                        { key: 'kind', header: t('wallet.col.kind'), render: (l: WalletAdminDetail['statement'][number]) => <span>{v('driver_wallet.statement_line_kind', l.kind)}</span> },
                        { key: 'amount', header: t('wallet.col.amount'), numeric: true, render: (l: WalletAdminDetail['statement'][number]) => <span className="tnum">{money(l.amount)}</span> },
                      ]}
                      rows={detail.statement}
                      rowKey={(l) => l.transactionId}
                      empty={note(t('wallet.statement.empty'))}
                    />

                    {mayManage && (
                      <>
                        <h4>{t('wallet.adjustment.title')}</h4>
                        <FormRow
                          action={
                            <Button type="button" disabled={adjIssue !== undefined || busy} onClick={() => void proposeAdjustment()}>{t('wallet.adjustment.propose')}</Button>
                          }
                        >
                          <Field label={t('wallet.adjustment.direction')} htmlFor="wallet-adj-direction">
                            <Select id="wallet-adj-direction" value={adjDirection} onChange={(e) => setAdjDirection(e.target.value === 'DEBIT' ? 'DEBIT' : 'CREDIT')}>
                              <option value="CREDIT">{v('ledger.entry_direction', 'CREDIT')}</option>
                              <option value="DEBIT">{v('ledger.entry_direction', 'DEBIT')}</option>
                            </Select>
                          </Field>
                          {isIso4217Currency(detail.currency) && (
                            <MoneyInput id="wallet-adj-amount" label={t('wallet.adjustment.amount')} currency={detail.currency} locale={lang} valueMinor={adjAmount} onChangeMinor={setAdjAmount} issueText={(i) => v('format.money_input_issue', i)} required />
                          )}
                          <Field label={t('wallet.adjustment.reason')} htmlFor="wallet-adj-reason">
                            <Select id="wallet-adj-reason" value={adjReason} onChange={(e) => setAdjReason((WALLET_ADJUSTMENT_REASON_CODES as readonly string[]).includes(e.target.value) ? (e.target.value as WalletAdjustmentReasonCode) : '')}>
                              <option value="">{t('wallet.adjustment.reason.choose')}</option>
                              {WALLET_ADJUSTMENT_REASON_CODES.map((r) => (
                                <option key={r} value={r}>{v('driver_wallet.wallet_adjustment_reason', r)}</option>
                              ))}
                            </Select>
                          </Field>
                          <Field label={t('wallet.note')} htmlFor="wallet-adj-note" hint={t('wallet.note.hint')}>
                            <TextInput id="wallet-adj-note" value={adjNote} maxLength={500} onChange={(e) => setAdjNote(e.target.value)} />
                          </Field>
                        </FormRow>
                        <h4>{t('wallet.ceiling.driverTitle')}</h4>
                        <CeilingProposal idPrefix="wallet-driver" currency={detail.currency} busy={busy} onPropose={(c, n) => void proposeDriverCeiling(c, n)} />
                      </>
                    )}

                    <h4>{t('wallet.detail.changes')}</h4>
                    <ul className="vello-admin-a-bullets">
                      {detail.adjustments.map((a) => (
                        <li key={a.adjustmentId}>
                          <Badge tone={walletChangeTone(a.status)}>{v('driver_wallet.wallet_change_status', a.status)}</Badge>{' '}
                          <span>{v('ledger.entry_direction', a.direction)} {money(a.amount)} · {v('driver_wallet.wallet_adjustment_reason', a.reasonCode)} · {when(a.proposedAt)}</span>
                        </li>
                      ))}
                      {detail.ceilingChanges.map((c) => (
                        <li key={c.changeId}>
                          <Badge tone={walletChangeTone(c.status)}>{v('driver_wallet.wallet_change_status', c.status)}</Badge>{' '}
                          <span>{t('wallet.ceiling.driverTitle')}: {c.ceiling === null ? t('wallet.ceiling.clear') : money(c.ceiling)} · {when(c.proposedAt)}</span>
                        </li>
                      ))}
                      {detail.adjustments.length + detail.ceilingChanges.length === 0 && <li>{t('wallet.changes.empty')}</li>}
                    </ul>
                          </>
                        )}
                      </DrawerStack>
                    </DrawerBody>
                    <DrawerFooter>
                      <Button type="button" variant="ghost" onClick={() => setOpenId(undefined)}>{t('ui.close')}</Button>
                    </DrawerFooter>
                  </>
                )}
              </Drawer>
            </>
          )}

          {tab === 'changes' && loaded !== '' && (
            <Panel padded={false}>
              <PanelBody>
                <Field label={t('wallet.changes.status')} htmlFor="wallet-change-status">
                  <Select id="wallet-change-status" value={changeStatus} onChange={(e) => void reloadChanges(e.target.value === 'APPLIED' ? 'APPLIED' : e.target.value === 'REJECTED' ? 'REJECTED' : 'PENDING')}>
                    {(['PENDING', 'APPLIED', 'REJECTED'] as const).map((s) => (
                      <option key={s} value={s}>{v('driver_wallet.wallet_change_status', s)}</option>
                    ))}
                  </Select>
                </Field>
              </PanelBody>
              <Table
                columns={[
                  { key: 'what', header: t('wallet.col.change'), render: (a: WalletAdjustmentView) => <span>{v('ledger.entry_direction', a.direction)} {money(a.amount)} · {v('driver_wallet.wallet_adjustment_reason', a.reasonCode)}{a.note !== null ? ` — ${a.note}` : ''}</span> },
                  { key: 'driver', header: t('wallet.col.driver'), render: (a: WalletAdjustmentView) => <PersonName id={a.driverId} /> },
                  { key: 'by', header: t('wallet.col.proposedBy'), render: (a: WalletAdjustmentView) => <span>{a.proposedByLabel ?? <PersonName id={a.proposedBy} />} · <span className="tnum">{when(a.proposedAt)}</span></span> },
                  { key: 'decision', header: t('wallet.col.decision'), render: (a: WalletAdjustmentView) => (a.status === 'PENDING' ? decisionCell('adjustment', a.adjustmentId, a) : <span>{v('driver_wallet.wallet_change_status', a.status)} · {a.decidedByLabel ?? <PersonName id={a.decidedBy} />}{a.decisionReason !== null ? ` — ${a.decisionReason}` : ''}<SelfApprovalNote record={a} /></span>) },
                ]}
                rows={changes?.adjustments ?? []}
                rowKey={(a) => a.adjustmentId}
                loading={changes === undefined}
                loadingLabel={t('ui.loading')}
                empty={note(t('wallet.changes.noAdjustments'))}
              />
              <Table
                columns={[
                  { key: 'what', header: t('wallet.col.change'), render: (c: WalletCeilingChangeView) => <span>{v('driver.wallet_ceiling_source', c.level)}: {c.ceiling === null ? t('wallet.ceiling.clear') : money(c.ceiling)}{c.note !== null ? ` — ${c.note}` : ''}</span> },
                  { key: 'subject', header: t('wallet.col.subject'), render: (c: WalletCeilingChangeView) => (c.driverId != null ? <PersonName id={c.driverId} /> : <Text role="caption" mono>{c.groupId ?? '—'}</Text>) },
                  { key: 'by', header: t('wallet.col.proposedBy'), render: (c: WalletCeilingChangeView) => <span>{c.proposedByLabel ?? <PersonName id={c.proposedBy} />} · <span className="tnum">{when(c.proposedAt)}</span></span> },
                  { key: 'decision', header: t('wallet.col.decision'), render: (c: WalletCeilingChangeView) => (c.status === 'PENDING' ? decisionCell('ceiling', c.changeId, c) : <span>{v('driver_wallet.wallet_change_status', c.status)} · {c.decidedByLabel ?? <PersonName id={c.decidedBy} />}{c.decisionReason !== null ? ` — ${c.decisionReason}` : ''}<SelfApprovalNote record={c} /></span>) },
                ]}
                rows={changes?.ceilingChanges ?? []}
                rowKey={(c) => c.changeId}
                loading={changes === undefined}
                loadingLabel={t('ui.loading')}
                empty={note(t('wallet.changes.noCeilings'))}
              />
            </Panel>
          )}

          {tab === 'packages' && loaded !== '' && (
            <>
              <Panel padded={false}>
                <Table
                  columns={[
                    { key: 'scope', header: t('wallet.col.scope'), render: (p: WalletCreditPackageView) => <span>{v('driver_wallet.package_scope', p.scope)} · {p.scopeRef}</span> },
                    { key: 'credit', header: t('wallet.col.credit'), numeric: true, render: (p: WalletCreditPackageView) => <span className="tnum">{money(p.credit)}</span> },
                    { key: 'price', header: t('wallet.col.price'), numeric: true, render: (p: WalletCreditPackageView) => <span className="tnum">{money(p.price)}</span> },
                    { key: 'discount', header: t('wallet.col.discount'), numeric: true, render: (p: WalletCreditPackageView) => <span className="tnum">{`${Math.floor(p.discountBasisPoints / 100)}%`}</span> },
                    { key: 'state', header: t('wallet.col.state'), render: (p: WalletCreditPackageView) => <InlineRow gap={2} align="center" wrap><Badge tone={p.active ? 'success' : 'neutral'}>{t(p.active ? 'wallet.package.active' : 'wallet.package.inactive')}</Badge><span className="tnum">v{p.version}</span></InlineRow> },
                    ...(mayManage ? [{ key: 'act', header: '', render: (p: WalletCreditPackageView) => <Button type="button" size="sm" disabled={busy} onClick={() => void savePackage(p, !p.active)}>{t(p.active ? 'wallet.package.deactivate' : 'wallet.package.activate')}</Button> }] : []),
                  ]}
                  rows={packages ?? []}
                  rowKey={(p) => p.packageId}
                  loading={packages === undefined}
                  loadingLabel={t('ui.loading')}
                  empty={note(t('wallet.package.empty'))}
                />
              </Panel>
              {mayManage && isIso4217Currency(currency) && (
                <Panel>
                  <h4>{t('wallet.package.new')}</h4>
                  <FormRow
                    action={
                      <Button type="button" disabled={pkgIssue !== undefined || busy} onClick={() => void savePackage()}>{t('wallet.package.save')}</Button>
                    }
                  >
                    <Field label={t('wallet.col.scope')} htmlFor="wallet-pkg-scope">
                      <Select id="wallet-pkg-scope" value={pkgScope} onChange={(e) => setPkgScope(e.target.value === 'country' ? 'country' : 'market')}>
                        <option value="market">{v('driver_wallet.package_scope', 'market')}</option>
                        <option value="country">{v('driver_wallet.package_scope', 'country')}</option>
                      </Select>
                    </Field>
                    <MoneyInput id="wallet-pkg-credit" label={t('wallet.col.credit')} currency={currency} locale={lang} valueMinor={pkgCredit} onChangeMinor={setPkgCredit} issueText={(i) => v('format.money_input_issue', i)} required />
                    <MoneyInput id="wallet-pkg-price" label={t('wallet.col.price')} currency={currency} locale={lang} valueMinor={pkgPrice} onChangeMinor={setPkgPrice} issueText={(i) => v('format.money_input_issue', i)} required hint={pkgCredit !== undefined && pkgPrice !== undefined && pkgIssue === undefined ? t('wallet.package.discountHint', { percent: String(packageDiscountPercent(pkgCredit, pkgPrice)) }) : undefined} />
                    <Field label={t('wallet.package.sort')} htmlFor="wallet-pkg-sort">
                      <TextInput id="wallet-pkg-sort" inputMode="numeric" value={pkgSort} onChange={(e) => setPkgSort(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} />
                    </Field>
                  </FormRow>
                  {pkgIssue === 'PRICE_ABOVE_CREDIT' && <Notice tone="warning">{t('wallet.refusal.priceAboveCredit')}</Notice>}
                </Panel>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export function DriverGroupsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v } = useVocab();
  const { mayManage, mayManageGroups } = walletAuthority(grants);
  const { market, scopeMarket, choices, refusal } = useWalletMarket();
  const [loaded, setLoaded] = useState('');
  const [groups, setGroups] = useState<readonly DriverGroupView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<DriverGroupView | undefined>(undefined);
  const [members, setMembers] = useState<readonly { driverId: string; addedAt: string }[] | undefined>(undefined);
  const [newMember, setNewMember] = useState('');
  const [currency, setCurrency] = useState('');
  // D502 (Q-006): the four-eyes proposals on groups that carry a ceiling of their own, and the reason typed for a rejection.
  const [changes, setChanges] = useState<readonly DriverGroupChangeView[]>([]);
  const [changeReason, setChangeReason] = useState('');
  // D838: the settlement mode proposed for the opened group (four eyes: another operator approves it in Financeiro › Acertos).
  const [groupMode, setGroupMode] = useState<SettlementModeCode | ''>('');
  // L6b-014: the settlement mode asked for at creation (also a proposal: another operator approves it in Financeiro › Acertos).
  const [newMode, setNewMode] = useState<SettlementModeCode | ''>('');
  const seq = useRef(0);

  const load = useCallback(
    async (m: string, background = false): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      // A background tick keeps the rows (and the opened group) on screen; a new market starts clean.
      if (!background) {
        setLoaded(target);
        setGroups(undefined);
        setChanges([]);
        setSelected(undefined);
        setMembers(undefined);
        setError(undefined);
      }
      if (target === '') return;
      const [res, pk, pending] = await Promise.all([runtime.wallet.groups(target), runtime.wallet.packages(target), runtime.wallet.groupChanges(target, 'PENDING')]);
      if (mine !== seq.current) return;
      if (pending.ok) setChanges(pending.value.changes);
      if (res.ok) {
        setError(undefined);
        setGroups(sortGroups(res.value.groups));
      } else setError(refusal(res.error, 'wallet.error'));
      setCurrency(pk.ok ? (pk.value.currency ?? '') : '');
    },
    [runtime, refusal],
  );

  // The groups follow the header scope's market and stay current in the background (no "load" button).
  useEffect(() => {
    void load(market);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const openGroup = async (g: DriverGroupView): Promise<void> => {
    setSelected(g);
    setMembers(undefined);
    const res = await runtime.wallet.members(loaded, g.groupId);
    if (res.ok) {
      setSelected(res.value.group);
      setMembers(res.value.members);
    } else setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
  };

  const refresh = async (text: string): Promise<void> => {
    setMsg({ tone: 'success', text });
    const keep = selected;
    await load(loaded);
    if (keep !== undefined) await openGroup(keep);
  };

  const act = async (fn: () => Promise<{ ok: boolean; value?: unknown; error?: { code: string; reasonCode?: string; detail?: string } }>, done: string): Promise<void> => {
    setBusy(true);
    const res = await fn();
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: refusal(res.error ?? { code: 'UNKNOWN' }, 'wallet.error') });
    // D502 (Q-006): a removal or an archive on a group with its own ceiling is only PROPOSED — say so, never "done".
    const pending = typeof res.value === 'object' && res.value !== null && (res.value as { pending?: unknown }).pending === true;
    await refresh(t(pending ? 'groups.change.pending' : done));
  };

  const decideChange = async (c: DriverGroupChangeView, approve: boolean): Promise<void> => {
    setBusy(true);
    const res = await runtime.wallet.decideGroupChange(c.changeId, { marketId: loaded, decision: approve ? 'APPROVE' : 'REJECT', ...(changeReason.trim() !== '' ? { reason: changeReason.trim() } : {}) });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: refusal(res.error, 'wallet.error') });
    setChangeReason('');
    await refresh(t('groups.changes.decided'));
  };

  const tz = marketTimeZone(loaded, markets);

  return (
    <>
      <PageHeader title={t('groups.title')} subtitle={t('groups.subtitle')} />
      {scopeMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
      <>
      {(error !== undefined || (msg !== undefined && selected === undefined)) && (
        <Panel>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
          {msg !== undefined && selected === undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
        </Panel>
      )}

      <Panel padded={false}>
        <Table
          columns={[
            { key: 'name', header: t('groups.col.name'), render: (g: DriverGroupView) => <span>{g.name}</span> },
            { key: 'status', header: t('groups.col.status'), render: (g: DriverGroupView) => <Badge tone={g.status === 'ACTIVE' ? 'success' : 'neutral'}>{v('driver_wallet.group_status', g.status)}</Badge> },
            { key: 'members', header: t('groups.col.members'), numeric: true, render: (g: DriverGroupView) => <span className="tnum">{g.memberCount}</span> },
            { key: 'settlement', header: t('groups.col.settlement'), render: (g: DriverGroupView) => <span>{g.settlementMode !== undefined && g.settlementMode !== null ? v('driver_wallet.settlement_mode', g.settlementMode) : t('groups.settlement.default')}</span> },
            { key: 'description', header: t('groups.col.description'), render: (g: DriverGroupView) => <span>{g.description ?? '—'}</span> },
          ]}
          rows={groups ?? []}
          rowKey={(g) => g.groupId}
          onRowClick={(g) => void openGroup(g)}
          {...(selected !== undefined ? { selectedKey: selected.groupId } : {})}
          loading={loaded !== '' && groups === undefined && error === undefined}
          loadingLabel={t('ui.loading')}
          error={error !== undefined ? t('ui.loadFailed') : undefined}
          onRetry={() => void load(market)}
          retryLabel={t('ui.retry')}
          empty={note(loaded === '' ? t('wallet.needsMarket') : t('groups.empty'))}
        />
      </Panel>

      {changes.length > 0 && (
        <Panel data-testid="group-changes">
          <h3>{t('groups.changes.title')}</h3>
          <Table
            columns={[
              { key: 'kind', header: t('groups.changes.col.kind'), render: (c: DriverGroupChangeView) => <span>{v('driver_wallet.group_change_kind', c.kind)}</span> },
              { key: 'group', header: t('groups.col.name'), render: (c: DriverGroupChangeView) => <span>{groups?.find((g) => g.groupId === c.groupId)?.name ?? <IdRef value={c.groupId} />}</span> },
              { key: 'driver', header: t('groups.changes.col.driver'), render: (c: DriverGroupChangeView) => <PersonName id={c.driverId} /> },
              { key: 'by', header: t('groups.changes.col.by'), render: (c: DriverGroupChangeView) => <span>{c.proposedByLabel ?? <PersonName id={c.proposedBy} />}</span> },
              ...(mayManageGroups
                ? [{
                    key: 'act', header: '',
                    render: (c: DriverGroupChangeView) => (
                      <InlineRow gap={2} align="center" wrap>
                        <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={() => void decideChange(c, true)} data-testid={`group-change-approve-${c.changeId}`}>{t('groups.changes.approve')}</Button>
                        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => void decideChange(c, false)} data-testid={`group-change-reject-${c.changeId}`}>{t('groups.changes.reject')}</Button>
                      </InlineRow>
                    ),
                  }]
                : []),
            ]}
            rows={changes}
            rowKey={(c) => c.changeId}
            empty={note(t('groups.changes.empty'))}
          />
          {mayManageGroups && (
            <Field label={t('groups.changes.reason')} htmlFor="group-change-reason">
              <TextInput id="group-change-reason" value={changeReason} maxLength={500} onChange={(e) => setChangeReason(e.target.value)} />
            </Field>
          )}
          <Text as="p" role="caption" tone="tertiary">{t('groups.changes.own')}</Text>
        </Panel>
      )}

      {mayManageGroups && loaded !== '' && (
        <Panel>
          <h4>{t('groups.new')}</h4>
          <FormRow
            action={
              <Button
                type="button"
                disabled={name.trim() === '' || busy}
                onClick={() =>
                  void act(async () => {
                    const r = await runtime.wallet.createGroup({ marketId: loaded, name: name.trim(), ...(description.trim() !== '' ? { description: description.trim() } : {}) });
                    if (!r.ok) return r;
                    setName('');
                    setDescription('');
                    if (newMode === '') return r;
                    // The group exists; its settlement mode is proposed on it at once (four eyes, as in the group's own panel).
                    const proposed = await runtime.settlement.proposeChange({ marketId: loaded, groupId: r.value.groupId, kind: 'ASSIGN_MODE', mode: newMode });
                    setNewMode('');
                    return proposed.ok ? r : proposed;
                  }, newMode === '' ? 'groups.created' : 'groups.created.mode')
                }
              >
                {t('groups.create')}
              </Button>
            }
          >
            <Field label={t('groups.col.name')} htmlFor="groups-name">
              <TextInput id="groups-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('groups.col.description')} htmlFor="groups-description">
              <TextInput id="groups-description" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label={t('groups.col.settlement')} htmlFor="groups-new-settlement" hint={t('groups.new.settlement.hint')}>
              <Select id="groups-new-settlement" value={newMode} onChange={(e) => setNewMode(e.target.value as SettlementModeCode | '')}>
                <option value="">{t('groups.settlement.default')}</option>
                {SETTLEMENT_MODES.map((m) => (
                  <option key={m} value={m}>{v('driver_wallet.settlement_mode', m)}</option>
                ))}
              </Select>
            </Field>
          </FormRow>
        </Panel>
      )}

      <Drawer open={selected !== undefined} onClose={() => setSelected(undefined)} labelledBy="groups-detail-title">
        {selected !== undefined && (
          <>
            <DrawerHeader titleId="groups-detail-title" title={selected.name} subtitle={<Badge tone={selected.status === 'ACTIVE' ? 'success' : 'neutral'}>{v('driver_wallet.group_status', selected.status)}</Badge>} onClose={() => setSelected(undefined)} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
                <Table
                  columns={[
                    { key: 'driver', header: t('wallet.col.driver'), render: (m: { driverId: string }) => <PersonName id={m.driverId} /> },
                    { key: 'since', header: t('groups.col.since'), render: (m: { addedAt: string }) => <span className="tnum">{formatMarketInstant(m.addedAt, tz, lang)}</span> },
                    ...(mayManageGroups ? [{ key: 'act', header: '', render: (m: { driverId: string }) => <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => runtime.wallet.removeMember(loaded, selected.groupId, m.driverId), 'groups.memberRemoved')}>{t('groups.remove')}</Button> }] : []),
                  ]}
                  rows={members ?? []}
                  rowKey={(m) => m.driverId}
                  loading={members === undefined}
                  loadingLabel={t('ui.loading')}
                  empty={note(t('groups.noMembers'))}
                />
                {mayManageGroups && selected.status === 'ACTIVE' && (
                  <FormRow
                    columns="minmax(0, 480px)"
                    action={
                      <Button
                        type="button"
                        disabled={!isDriverId(newMember) || busy}
                        onClick={() =>
                          void act(async () => {
                            const r = await runtime.wallet.addMember(selected.groupId, { marketId: loaded, driverId: newMember.trim() });
                            if (r.ok) setNewMember('');
                            return r;
                          }, 'groups.memberAdded')
                        }
                      >
                        {t('groups.add')}
                      </Button>
                    }
                  >
                    <Field label={t('groups.addMember')} htmlFor="groups-member" hint={t('groups.addMember.hint')}>
                      <MonoInput id="groups-member" value={newMember} onChange={(e) => setNewMember(e.target.value)} />
                    </Field>
                  </FormRow>
                )}
                {mayManage && selected.status === 'ACTIVE' && (
                  <>
                    <h4>{t('wallet.ceiling.groupTitle')}</h4>
                    {currency === '' ? (
                      <Notice tone="warning">{t('wallet.refusal.currency')}</Notice>
                    ) : (
                      <CeilingProposal
                        idPrefix="groups"
                        currency={currency}
                        busy={busy}
                        onPropose={(ceilingMinor, n) => void act(() => runtime.wallet.proposeCeiling({ marketId: loaded, level: 'GROUP', groupId: selected.groupId, ceilingMinor, note: n }), 'wallet.ceiling.proposed')}
                      />
                    )}
                    <Text as="p" role="caption" tone="tertiary">{t('wallet.ceiling.fourEyes')}</Text>
                    <h4>{t('groups.settlement.title')}</h4>
                    <Text as="p" role="caption" tone="secondary" data-testid="groups-settlement-current">
                      {selected.settlementMode !== undefined && selected.settlementMode !== null ? v('driver_wallet.settlement_mode', selected.settlementMode) : t('groups.settlement.default')}
                    </Text>
                    <FormRow
                      columns="minmax(0, 480px)"
                      action={
                        <InlineRow gap={2}>
                          <Button
                            type="button"
                            disabled={busy || groupMode === ''}
                            data-testid="groups-settlement-propose"
                            onClick={() => void act(() => runtime.settlement.proposeChange({ marketId: loaded, groupId: selected.groupId, kind: 'ASSIGN_MODE', mode: groupMode as SettlementModeCode }), 'groups.settlement.proposed')}
                          >
                            {t('groups.settlement.propose')}
                          </Button>
                          {selected.settlementMode !== undefined && selected.settlementMode !== null && (
                            <Button type="button" variant="ghost" disabled={busy} data-testid="groups-settlement-clear" onClick={() => void act(() => runtime.settlement.proposeChange({ marketId: loaded, groupId: selected.groupId, kind: 'CLEAR_MODE' }), 'groups.settlement.proposed')}>
                              {t('groups.settlement.clear')}
                            </Button>
                          )}
                        </InlineRow>
                      }
                    >
                      <Field label={t('groups.col.settlement')} htmlFor="groups-settlement-mode">
                        <Select id="groups-settlement-mode" value={groupMode} onChange={(e) => setGroupMode(e.target.value as SettlementModeCode | '')}>
                          <option value="">—</option>
                          {SETTLEMENT_MODES.map((m) => (
                            <option key={m} value={m}>{v('driver_wallet.settlement_mode', m)}</option>
                          ))}
                        </Select>
                      </Field>
                    </FormRow>
                    <Text as="p" role="caption" tone="tertiary">{t('groups.settlement.fourEyes')}</Text>
                  </>
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button type="button" variant="ghost" onClick={() => setSelected(undefined)}>{t('ui.close')}</Button>
              {mayManageGroups && (
                <Button type="button" disabled={busy} onClick={() => void act(() => runtime.wallet.updateGroup(selected.groupId, { marketId: loaded, status: selected.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE' }), 'groups.updated')}>
                  {t(selected.status === 'ACTIVE' ? 'groups.archive' : 'groups.restore')}
                </Button>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
      </>
      )}
    </>
  );
}
